import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Tag as TagIcon, Palette } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useCreateTag, useUpdateTag } from '@utils/api/organizations/tags';
import { Tag, CreateTagPayload } from '@utils/types/organization/tag.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context';

/* eslint-disable no-restricted-syntax -- Preset tag palette must stay constant across themes */
const TAG_COLOR_PRESETS = [
  '#FEE047',
  '#F9A8D4',
  '#86EFAC',
  '#93C5FD',
  '#FCA5A5',
  '#C4B5FD',
  '#FDBA74',
  '#5EEAD4'
];

interface TagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag?: Tag | null;
}

interface FormData {
  name: string;
  color?: string;
}

export default function TagFormModal({
  isOpen,
  onClose,
  tag
}: TagFormModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const isEditing = !!tag;

  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(t('tag-name-is-required') || 'Tag name is required')
      .max(100, t('tag-name-max-length') || 'Max 100 characters'),
    color: Yup.string().matches(
      /^#?[0-9A-Fa-f]{6}$/,
      t('invalid-color') || 'Color must be a 6-digit hex'
    )
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      color: TAG_COLOR_PRESETS[0]
    }
  });

  const selectedColor = watch('color');

  useEffect(() => {
    if (isOpen) {
      if (tag) {
        reset({
          name: tag.name || '',
          color: tag.color || TAG_COLOR_PRESETS[0]
        });
      } else {
        reset({
          name: '',
          color: TAG_COLOR_PRESETS[0]
        });
      }
    }
  }, [isOpen, tag, reset]);

  const onSubmit = async (data: FormData) => {
    const normalizedColor = data.color
      ? data.color.startsWith('#')
        ? data.color
        : `#${data.color}`
      : null;

    const payload: CreateTagPayload = {
      name: data.name.trim(),
      color: normalizedColor
    };

    try {
      if (isEditing && tag) {
        await updateMutation.mutateAsync({ tagId: tag.id, payload });
        toast.success(t('tag-updated-successfully') || 'Tag updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t('tag-created-successfully') || 'Tag created');
      }
      onClose();
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 400) {
        toast.error(t('tag-name-already-exists') || 'Tag name already exists');
      } else {
        toast.error(
          isEditing
            ? t('failed-to-update-tag') || 'Failed to update tag'
            : t('failed-to-create-tag') || 'Failed to create tag'
        );
      }
    }
  };

  const isLoading =
    isSubmitting || createMutation.isLoading || updateMutation.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing ? t('edit-tag') || 'Edit tag' : t('add-tag') || 'Add tag'
      }
      caption={
        isEditing
          ? t('update-tag-details') || 'Update tag details'
          : t('add-a-new-tag-to-directory') || 'Create a new named highlight'
      }
      customWidth="520px"
      footerActions={[
        <Button
          key="cancel"
          variant="secondary"
          onClick={onClose}
          label={t('cancel')}
          disabled={isLoading}
        />,
        <Button
          key="submit"
          onClick={handleSubmit(onSubmit)}
          label={
            isEditing
              ? t('save-changes') || 'Save changes'
              : t('create-tag') || 'Create tag'
          }
          loading={isLoading}
          disabled={isLoading}
        />
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <span className="flex items-center gap-2">
                  <TagIcon
                    size={14}
                    style={{ color: getColour('activeText') }}
                  />
                  {t('name')}
                </span>
              }
              placeholder={t('enter-tag-name') || 'Enter tag name'}
              error={errors.name?.message}
              required
              autoFocus
            />
          )}
        />

        <div>
          <label
            className="text-sm flex items-center gap-2 mb-2"
            style={{ color: getColour('normalText') }}
          >
            <Palette size={14} style={{ color: getColour('inactiveText') }} />
            {t('color') || 'Color'}
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {TAG_COLOR_PRESETS.map(preset => (
              <button
                key={preset}
                type="button"
                onClick={() => setValue('color', preset)}
                className="w-7 h-7 rounded-full transition-all duration-200 cursor-pointer hover:scale-110"
                style={{
                  backgroundColor: preset,
                  border:
                    selectedColor?.toLowerCase() === preset.toLowerCase()
                      ? `2px solid ${getColour('activeText')}`
                      : `1px solid ${getColour('border')}`
                }}
                aria-label={preset}
              />
            ))}
          </div>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="#FEE047"
                error={errors.color?.message}
                helperText={
                  t('tag-color-helper') ||
                  'Use a 6-digit hex (e.g. #FEE047) or pick from above.'
                }
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}
